import React, { useCallback, useEffect, useRef, useState } from 'react'
import QuizContent from '../shared/QuizContent'
import GoogleIcon from '../../assets/google'
import { createPreLanderAccount, getGoogleAuthUrl, createModelParams, convertSelectionsToApiFormat, collectAndStoreUTMData, getExternalClickIds, submitEmailOnly, LP_SOURCE } from '../../utils/api' // Use main API service
import { getEfTransactionId } from '../../utils/everflow'
import { useQuizSelections } from '../../hooks/useLocalStorage'
import TermsOfService from './termsOfService'
import PrivacyPolicy from './privacyPolicy'
import { useFormValidation, validationRules } from '../../hooks/useFormValidation'
import { buildPaywallUrl, buildOAuthStatePath, resolvePaywallPath } from '../../utils/paywall'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { trackRegistrationCompleted , trackRegistrationStarted, trackPaywallPlanSelected, trackLoginCompleted } from '../../utils/analytics'
import { trackServerRegistrationStarted, getUtmAndEf, getRegistrationAttribution } from '../../utils/serverGtm'
import { trackRegistrationStartedMixpanel, trackRegistrationCompletedMixpanel, getUserIdFromAccessToken } from '../../utils/mixpanel'
import {
  PaywallPopup,
  runUpgateCheckout,
  trackPopupOrderCreated,
  getSelectedPlan,
  setSelectedPlan,
  clearSelectedPlan,
  isAuthCheckoutError,
} from '../paywall-popup'
import {
  saveLpSession,
  readLpSession,
  clearLpSession,
  markOauthReturnHandled,
  wasOauthReturnHandled,
} from '../../utils/session'
import { useGrowthBookValues } from '../../contexts/GrowthBookContext'
import OtpStep from './otpStep'

const Step14 = ({ onContinue, onSuccessContinue = null }) => {
  const location = useLocation()
  const isQuiz = !!location.pathname.includes('/quiz')
  const { magicLink, paywallNavType, paywallFlow, paywallOrder, paywallPopupVideo, premiumTokensAmount, premiumInstantTokens, isFlatRebill, priceMultiplier } = useGrowthBookValues()
  const isMagicLink = magicLink === 'ON'
  // GrowthBook flags forwarded with auth/registration calls so the backend can
  // attribute conversions per A/B variant. Mirrors the sibling create-friend
  // lander's `ab_testing` object (this lander's magic-link flag is white-specific).
  // All three nav-config fields (type/flow/order) are sent so the marketing payload
  // the backend stores — and replays to Mixpanel — identifies the exact arm.
  const ab_testing = {
    'createsexfriends-white-magicLink': magicLink,
    'createsexfriends-white-paywall-nav-config': paywallNavType,
    'createsexfriends-white-paywall-flow': paywallFlow,
    'createsexfriends-white-paywall-order': paywallOrder,
    // premium-tokens-amount / premium-instant-tokens-amount drive the paywall
    // popup's token-bonus pill. Forward them (as strings, per the ab_testing
    // contract) ONLY in the popup flow so backend + Mixpanel can attribute
    // conversions to the token-bonus variant the user actually saw.
    ...(paywallNavType === 'paywall_popup'
      ? {
          'premium-tokens-amount': String(premiumTokensAmount ?? ''),
          'premium-instant-tokens-amount': String(premiumInstantTokens ?? '0|0|0'),
        }
      : {}),
  }
  // Paywall-popup renders IN PLACE after signup only in the standard (AuthModal)
  // flow — the /quiz flow (onSuccessContinue) keeps its own continue behaviour.
  const usePaywallPopup = paywallNavType === 'paywall_popup' && !onSuccessContinue
  // order 'popup_first': the popup gates this screen instead of following it —
  //   popup (anonymous, plan capture only) → signup form → Upgate checkout.
  // The charge can't run at the Continue tap (no authToken yet), so the plan is
  // persisted and the checkout fires the moment registration returns a token, from
  // startCheckoutWithPlan() below. 'after_signup' (default) is untouched.
  const isPopupFirst = usePaywallPopup && paywallOrder === 'popup_first'
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [emailError, setEmailError] = useState('')
  const [showTerms, setShowTerms] = useState(false)
  const [showPrivacy, setShowPrivacy] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [otpState, setOtpState] = useState(null)
  // Holds the registration tokens for the in-place paywall popup (paywall_popup flow).
  const [popupState, setPopupState] = useState(null)
  // popup_first: the plan picked on the pre-signup popup. Seeded from sessionStorage
  // so the Google OAuth return (a fresh page load) still knows what to charge, and so
  // a user who navigates back to /signup isn't asked to pick again.
  const [selectedPlan, setSelectedPlanState] = useState(() =>
    isPopupFirst ? getSelectedPlan() : null,
  )
  // popup_first: registration succeeded and we're creating the Upgate order — the
  // form is replaced by a spinner until window.location leaves for the hosted page.
  const [isOpeningCheckout, setIsOpeningCheckout] = useState(false)
  // External click-ids (gclid/fbclid/twclid/ttclid) resolved once, threaded onto the
  // sGTM/dataLayer popup events so they carry the same ad-attribution as the platform.
  const [clickIds, setClickIds] = useState(null)
  const [selections] = useQuizSelections() // Get quiz selections
  const googleBtnRef = useRef(null)

  // ---- Returning visitor ----------------------------------------------------
  // Read ONCE at mount (a fresh page load is exactly the case this exists for):
  // whoever registered in the last hour left a session behind (utils/session.js).
  // With an authToken it is resumable — the popup arm can check out again without
  // re-registering. Without one (the redirect arm spends its token on the
  // platform, or a resumed checkout 401'd) it only carries the email, which
  // prefills the form so the user isn't retyping it.
  const storedSessionRef = useRef(readLpSession())
  // The still-spendable token (popup arm only — the redirect arm's is consumed by
  // the platform on arrival). Drives the resume effect below, and suppresses the
  // pre-registration plan popup: without that the plan-picking popup would render
  // for the frame before the effect swaps in the token-backed one, and a Continue
  // in that frame would ask for a plan instead of checking out.
  const resumableToken = usePaywallPopup
    ? storedSessionRef.current?.authToken
    : undefined

  // Resolve external click-ids once on mount (reads landing params from sessionStorage).
  useEffect(() => {
    getExternalClickIds().then(setClickIds).catch(() => {})
  }, [])

  // ---- popup_first: plan capture → post-registration checkout ----------------

  // The pre-signup popup's Continue tap. No charge is possible yet (anonymous), so
  // remember the choice (+ the display values the checkout texts need) and reveal the
  // signup form underneath. order_created is deliberately NOT emitted here — see
  // utils/analytics.js; this tap emits paywall_popup_plan_selected instead.
  const handlePlanPicked = useCallback(
    (
      plan,
      {
        bonusTokens,
        currencySymbol,
        currencyCode,
        locale,
        isFlatRebill: pickedFlatRebill,
        priceMultiplier: pickedPriceMultiplier,
      },
    ) => {
      // The rebill variant and the geo multiplier are frozen with the rest of the
      // display values: the charge happens on the next page load (OAuth return), where
      // GB re-resolves from scratch — and the multiplier additionally depends on a geo
      // lookup that has to run again there.
      const selection = {
        plan,
        bonusTokens,
        currencySymbol,
        currencyCode,
        locale,
        isFlatRebill: pickedFlatRebill,
        priceMultiplier: pickedPriceMultiplier,
      }
      setSelectedPlan(selection)
      setSelectedPlanState(selection)
      trackPaywallPlanSelected({
        plan_id: plan._id,
        value: Number(plan.paymentAmount),
        currency: currencyCode,
        duration_months: plan.duration_months,
        'paywall-type': paywallNavType,
        'paywall-order': paywallOrder,
      })
    },
    [paywallNavType, paywallOrder],
  )

  // Open the Upgate checkout for the already-picked plan, now that registration has
  // produced an authToken. Returns true when the redirect is under way.
  //
  // On ANY failure (no stored plan, checkout error, no redirectUrl) we fall back to
  // the after_signup behaviour: render the popup in place with the token we now hold,
  // so the user can pick again and retry instead of being stranded on the LP.
  const startCheckoutWithPlan = useCallback(
    async ({ authToken, accessToken, userId, email: userEmail }) => {
      const stored = getSelectedPlan() || selectedPlan
      const openPopupInstead = () => {
        clearSelectedPlan()
        setSelectedPlanState(null)
        setIsOpeningCheckout(false)
        setPopupState({ authToken, accessToken, userId })
      }
      if (!stored?.plan?._id || !authToken) {
        openPopupInstead()
        return false
      }

      setIsOpeningCheckout(true)
      // order_created fires HERE, not at the popup's Continue tap: this is the point
      // where checkout is actually opened, and only now do we have the Mongo _id
      // Mixpanel needs — which also keeps registration_started → order_created in
      // funnel order for this arm.
      trackPopupOrderCreated({
        plan: stored.plan,
        currencyCode: stored.currencyCode,
        paywallType: paywallNavType,
        paywallOrder,
        userId,
        email: userEmail,
        clickIds,
      })
      try {
        const data = await runUpgateCheckout(stored.plan, {
          authToken,
          bonusTokens: stored.bonusTokens,
          currencySymbol: stored.currencySymbol,
          locale: stored.locale,
          // Rides onto the /lobby return URL so the platform tags
          // subscription_purchase with the experiment variant.
          paywallType: paywallNavType,
          // Live values are the fallback only — for a pick stored before these fields existed.
          isFlatRebill: stored.isFlatRebill ?? isFlatRebill,
          priceMultiplier: stored.priceMultiplier ?? priceMultiplier,
        })
        if (!data?.redirectUrl) throw new Error('checkout returned no redirectUrl')
        // runUpgateCheckout already navigated — keep the spinner up until unload.
        return true
      } catch (err) {
        console.error('[popup_first] checkout failed', err)
        openPopupInstead()
        return false
      }
    },
    [selectedPlan, paywallNavType, paywallOrder, clickIds, isFlatRebill, priceMultiplier],
  )

  // popup_first: show the plan popup until a plan is picked. Skipped once we hold a
  // pick (incl. the OAuth return, where sessionStorage rehydrates it) and while the
  // post-registration checkout / fallback popup is on screen.
  const showPlanPopup =
    isPopupFirst && !resumableToken && !selectedPlan && !popupState && !isOpeningCheckout

  // Use the custom validation hook.
  const { values, validateAll, getFieldProps } = useFormValidation(
    { email: storedSessionRef.current?.email || '', password: '' },
    {
      email: validationRules.email,
      password: validationRules.password,
    },
  )

  // useEffect(() => {
  //   const setupGoogleAuth = async () => {
  //     if (googleBtnRef.current) {
  //       try {
  //         const googleAuthUrl = await getGoogleAuthUrl(selections);
  //         googleBtnRef.current.href = googleAuthUrl;
  //       } catch (error) {}
  //     }
  //   };

  //   setupGoogleAuth();
  // }, [selections]);

  useEffect(() => {
    // Guards the async href write below against overlapping/superseded runs: when
    // premiumTokensAmount (or selections/navType) resolves mid-flight the effect
    // re-runs, and without this an earlier run finishing its awaits later could
    // clobber the href with a STALE ab_testing (e.g. an empty premium token) —
    // sending the wrong value to the backend while the returning popup shows the
    // sticky variant. Superseded runs bail before touching the button.
    let cancelled = false
    const utmObj = collectAndStoreUTMData()
    console.log(utmObj)
    if (!googleBtnRef.current) return

    if (isQuiz) {
      // /quiz: defer the actual Google OAuth until the final step so the user stays on the
      // prelander host through the quiz. The OAuth fires from QuizFinal with full
      // model-params + path: '/paywall', so backend redirects straight to ud824.com/paywall.
      googleBtnRef.current.href = '#'
      googleBtnRef.current.onclick = e => {
        e.preventDefault()
        // Wipe any stale token so the deferred-Google branch on the final step actually fires.
        trackRegistrationStarted('google')
        localStorage.removeItem('quizAuthToken')
        localStorage.removeItem('authToken')
        localStorage.setItem('pendingGoogleSignup', 'true')
        localStorage.setItem('pendingRegistrationSource', 'google')
        localStorage.setItem('googleAuthInProgress', 'true')
        localStorage.setItem('googleAuthStartTime', Date.now().toString())
        sessionStorage.setItem('quizSessionAuthed', 'true')
        console.log('[quiz] Google deferred — pendingGoogleSignup set, advancing quiz')
        if (onSuccessContinue) {
          onSuccessContinue({ authToken: null })
        }
      }
      return
    }

    // Mirrors the sibling create-friend lander's Google OAuth state assembly:
    // utm + ef_transaction_id (conditional spread) + externalClickIds at the top level of
    // state, then JSON.stringify into the `?state=` query. Encode protection=sfw into `path`
    // so it ends up on the post-OAuth paywall URL — backend builds the final URL via
    // `new URL(BASE + path)`, which parses the embedded query string correctly.
    const setupGoogleAuth = async () => {
      const efTransactionId = getEfTransactionId()
      const externalClickIds = await getExternalClickIds()
      if (cancelled || !googleBtnRef.current) return
      const finalState = {
        ...utmObj,
        ab_testing: JSON.stringify(ab_testing),
        lp_source: LP_SOURCE,
        ...(efTransactionId ? { ef_transaction_id: efTransactionId } : {}),
        externalClickIds,
        // chat_paywall → /lobby?protection=sfw; else /paywall?protection=sfw.
        path: buildOAuthStatePath(paywallNavType),
        // paywall_popup: return to THIS lander URL (?auth_token) so the popup renders
        // in place after OAuth, instead of the backend handing off to /paywall.
        ...(usePaywallPopup
          ? { returnUrl: `${window.location.origin}${window.location.pathname}` }
          : {}),
      }
      // chat_paywall + paywall_popup must carry the model slug (both end at /lobby) —
      // backend reads model-params from state.
      if ((paywallNavType === 'chat_paywall' || usePaywallPopup) && selections) {
        try {
          finalState['model-params'] = await createModelParams(selections)
          finalState.type = selections.welcome === 'Anime' ? 'anime' : 'realistic'
        } catch (err) {
          console.error('Error creating model params for Google auth', err)
        }
      }
      const googleAuthUrl = getGoogleAuthUrl()
      const oauthUrl = `${googleAuthUrl}?state=${encodeURIComponent(JSON.stringify(finalState))}`
      // A newer run (freshly-resolved premium/nav) may have superseded us during
      // the awaits above — don't clobber its href with our now-stale ab_testing.
      if (cancelled || !googleBtnRef.current) return
      googleBtnRef.current.href = oauthUrl
      googleBtnRef.current.onclick = e => {
        e.preventDefault()
        // Regular (redirect) flow fires registration_started at the click. For
        // paywall_popup we DON'T fire here — a keepalive fetch right before the OAuth
        // navigation is often dropped, so the reliable copy is emitted on the OAuth
        // RETURN instead (see the auth_token effect below).
        if (!usePaywallPopup) trackRegistrationStarted('google')
        localStorage.setItem('googleAuthInProgress', 'true')
        localStorage.setItem('googleAuthStartTime', Date.now().toString())
        localStorage.setItem('pendingRegistrationSource', 'google')
        window.location.assign(oauthUrl)
      }
    }
    setupGoogleAuth()
    // Cancel superseded runs so a late-finishing stale async href write can't
    // clobber a newer one. premiumTokensAmount/premiumInstantTokens are in the
    // deps so the OAuth state (which embeds ab_testing) is rebuilt when GrowthBook
    // re-resolves them, and this cleanup fires before each re-run.
    return () => {
      cancelled = true
    }
    // showPlanPopup is a dep because in popup_first the form (and so the Google
    // button ref) doesn't exist on the first render — the popup is rendered instead.
    // Without it the effect bails on the null ref and never re-runs, leaving the
    // Google button href at '#'.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isQuiz, paywallNavType, selections, premiumTokensAmount, premiumInstantTokens, showPlanPopup])

  // ---- Returning-visitor resume ---------------------------------------------
  // Reopen the paywall popup with the token the visitor already holds (they
  // registered, went to Upgate, backed out) instead of the signup form. No
  // registration events fire — this is the SAME registration, not a new one.
  // Runs in an effect (not at mount) because GrowthBook resolves async: the nav
  // config is 'paywall' on the first render and only becomes 'paywall_popup' once
  // GB (or the sticky sessionStorage snapshot) resolves.
  const resumeHandledRef = useRef(false)
  useEffect(() => {
    if (resumeHandledRef.current) return
    // resumableToken is undefined in the redirect arm (token already spent) and
    // until GrowthBook resolves the nav config.
    if (!resumableToken) return
    if (popupState || isOpeningCheckout) return
    // A Google OAuth return carries a FRESHER token on the URL — its own effect
    // owns that path (and fires the registration events for it).
    if (new URLSearchParams(window.location.search).get('auth_token')) return
    resumeHandledRef.current = true
    setPopupState({
      authToken: resumableToken,
      userId: storedSessionRef.current?.userId,
      email: storedSessionRef.current?.email,
      resumed: true,
    })
  }, [resumableToken, popupState, isOpeningCheckout])

  // Returning from the hosted checkout. A fresh load is covered by the mount-time
  // read + the effect above, but browser Back usually restores this page from the
  // back/forward cache instead: JS never re-runs, so nothing above fires and React
  // state stays frozen exactly as we left it — the submit button stuck on
  // "Creating…", the popup_first spinner, or the signup form that was up when the
  // redirect happened (which is what made a returning user look un-registered).
  // Re-read the session on every return to the page and put the popup back.
  // `pageshow` covers the bfcache restore; visibilitychange covers an in-app
  // browser handing the tab back without one. Keyed on checkoutAt so a
  // dismissed/finished attempt isn't reopened on every alt-tab, while a SECOND
  // trip to Upgate is.
  const resumedCheckoutAtRef = useRef(null)
  useEffect(() => {
    const resume = () => {
      const fresh = readLpSession()
      storedSessionRef.current = fresh
      if (!fresh?.authToken || !fresh.checkoutAt) return
      if (!usePaywallPopup) return
      if (resumedCheckoutAtRef.current === fresh.checkoutAt) return
      resumedCheckoutAtRef.current = fresh.checkoutAt
      resumeHandledRef.current = true
      setIsLoading(false)
      setIsOpeningCheckout(false)
      setPopupState({
        authToken: fresh.authToken,
        userId: fresh.userId,
        email: fresh.email,
        resumed: true,
      })
    }
    const onPageShow = event => {
      if (event.persisted) resume()
    }
    const onVisibility = () => {
      if (document.visibilityState === 'visible') resume()
    }
    window.addEventListener('pageshow', onPageShow)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.removeEventListener('pageshow', onPageShow)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [usePaywallPopup])

  // The stored token is dead — spent by the platform (a completed checkout
  // consumes it at /lobby) or aged out of the backend's 1h Redis window. Drop the
  // token but KEEP the email (the account exists) and fall back to the auth form;
  // anything else (network blip, 5xx) stays retryable on the popup's Continue.
  const handleResumeCheckoutError = err => {
    if (!isAuthCheckoutError(err)) return
    const stored = storedSessionRef.current
    if (stored?.email) {
      const tokenless = { email: stored.email, source: stored.source }
      saveLpSession(tokenless)
      storedSessionRef.current = tokenless
    } else {
      clearLpSession()
      storedSessionRef.current = null
    }
    setPopupState(null)
    // Password users go to the login card — the signup form can only answer
    // "email already used". Google/magic-link stay on this card: its Google button
    // re-authenticates an existing account and its email-only submit recovers one
    // via OTP. The email is already prefilled either way.
    if (!isMagicLink && stored?.source === 'password' && onContinue) onContinue()
  }

  // Google popup return: backend sent ?auth_token back to this lander — reopen the
  // popup (after_signup) or fire the checkout for the pre-picked plan (popup_first).
  const oauthReturnHandledRef = useRef(false)
  useEffect(() => {
    if (popupState || isOpeningCheckout) return
    if (oauthReturnHandledRef.current) return
    const params = new URLSearchParams(window.location.search)
    const authToken = params.get('auth_token')
    if (!authToken) return
    // uid = Mongo _id from the return URL — the only identity the Google return
    // carries; used to identify the Mixpanel order_created inside the popup.
    const uid = params.get('uid')
    // A popup return is proven by the URL, not by the GB-resolved nav config: GB
    // cannot re-resolve `paywall_popup` on a return leg where its sticky
    // sessionStorage value is gone (cross-browser or in-app-webview OAuth, a new
    // tab, private mode). Keying only on `usePaywallPopup` there leaves the token
    // unconsumed in the URL. `uid` is ridden back only on a popup returnUrl, and
    // the non-popup Google flow never returns to this lander, so it cannot hijack
    // another flow. Same discriminator as the sibling landers.
    if (!uid && !usePaywallPopup) return
    // `is_new=0` → this Google user ALREADY had an account (abandoned an Upgate
    // checkout and came back, re-clicked the ad, lost the tab's sessionStorage).
    // The backend returns them to this same popup instead of the platform
    // /paywall, so the flow below is identical — but the registration events must
    // not fire, or every repeat Google login counts as a new registration.
    // Absent marker = new: the only case a backend without this param produces.
    const isReturningUser = params.get('is_new') === '0'
    // The effect re-runs when clickIds resolves; the tracking below must stay
    // exactly-once even if that lands before the state guards above flip.
    oauthReturnHandledRef.current = true
    const stripAuthParams = () => {
      try {
        const url = new URL(window.location.href)
        ;['auth_token', 'uid', 'is_new'].forEach(k => url.searchParams.delete(k))
        window.history.replaceState({}, '', url.toString())
      } catch (e) {
        // ignore
      }
    }

    // Coming BACK to this return URL rather than arriving at it: browser Back from
    // Upgate, a reload, a restore that resurrects the pre-strip URL. Registration
    // already happened and checkout already ran once, so re-running the block
    // below would double-fire the registration events and — the symptom users
    // report — bounce them straight back into Upgate instead of letting them
    // change anything. Reopen the pricing popup in its normal pick-a-plan mode,
    // wired to the same token: Continue goes to checkout with no second
    // registration and no email prompt. `checkoutAt` covers a tab where the
    // session survived but the handled-marker did not.
    const freshSession = readLpSession()
    if (wasOauthReturnHandled(authToken) || freshSession?.checkoutAt) {
      stripAuthParams()
      setPopupState({ authToken, userId: uid, resumed: true })
      return
    }
    // Claim the token BEFORE the work below: it ends in a checkout that navigates
    // away, and a Back into this same entry must find it already claimed.
    markOauthReturnHandled(authToken)
    // registration_started emitted HERE on the OAuth return (not at the click): a
    // keepalive fetch at click races the navigation to Google and is often dropped.
    // A returning Google user did not register — the backend matched an existing
    // account and reissued a checkout token. Report the login instead; the backend
    // fires its own landing_page_login, and Mixpanel's registration_completed is
    // deduped by $insert_id, not by "did this registration actually happen".
    if (isReturningUser) {
      trackLoginCompleted()
    } else {
      trackRegistrationStarted({
        'paywall-type': paywallNavType,
        'paywall-order': paywallOrder,
        flow: 'paywall_popup',
        source: 'google',
        ...(clickIds || {}),
        ...getUtmAndEf(),
      })
      trackServerRegistrationStarted({
        flow: 'paywall_popup',
        source: 'google',
        paywallType: paywallNavType,
        paywallOrder,
        clickIds,
      })
      trackRegistrationCompleted('google')
    }
    // registration_completed (Google) — the backend rides the Mongo _id on the return
    // URL as `uid`, so we can send the identified event; it dedupes with the backend's
    // own copy via the shared $insert_id (utils/insertId.js).
    if (uid && !isReturningUser) {
      trackRegistrationCompletedMixpanel(
        uid,
        {
          source: 'google',
          auth_type: 'google',
          'paywall-type': paywallNavType,
          'paywall-order': paywallOrder,
        },
        ab_testing,
        undefined,
        getRegistrationAttribution(),
      )
    }

    // If the user backs out of the Upgate page they land on a fresh LP with no
    // React state, and this is what stops the signup form asking them to register
    // again (utils/session.js).
    saveLpSession({ authToken, userId: uid || undefined, source: 'google' })
    // Strip the auth params BEFORE the checkout below navigates away, so a
    // browser Back can't land on a URL that re-runs this effect.
    stripAuthParams()

    // popup_first: the plan was picked before the OAuth hop (rehydrated from
    // sessionStorage) — go straight to Upgate instead of showing the popup again.
    if (isPopupFirst) {
      startCheckoutWithPlan({ authToken, userId: uid })
      return
    }
    setPopupState({ authToken, userId: uid })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usePaywallPopup, popupState, isOpeningCheckout, clickIds, isPopupFirst, startCheckoutWithPlan])

  useEffect(() => {
    const pendingSource = localStorage.getItem('pendingRegistrationSource')
    const isCompleted = localStorage.getItem('isCompleted')

    if (pendingSource === 'google' && isCompleted) {
      trackRegistrationCompleted('google')
      localStorage.removeItem('pendingRegistrationSource')
    }
  }, [])

  const handleEmailOnlySubmit = async () => {
    const emailErr = validationRules.email(values.email)
    if (emailErr) {
      setEmailError(emailErr)
      return
    }
    setEmailError('')
    setIsLoading(true)
    setError('')
    try {
      const data = await submitEmailOnly(values.email, { selections, ab_testing })

      if (data?.success === false) {
        setEmailError(data.message || 'Email already used')
        return
      }

      if (data?.otp_sent) {
        setOtpState({ email: values.email, otpExpiry: data.otpExpiry })
        return
      }

      // Magic-link new email + paywall_popup (standard signup): open the popup in place.
      if (data?.authToken && usePaywallPopup) {
        // registration_started BEFORE completed (funnel order): dataLayer + sGTM copies.
        trackRegistrationStarted({
          'paywall-type': paywallNavType,
          'paywall-order': paywallOrder,
          flow: 'paywall_popup',
          source: 'magic_link',
          email: values.email,
          ...(clickIds || {}),
          ...getUtmAndEf(),
        })
        trackServerRegistrationStarted({
          flow: 'paywall_popup',
          source: 'magic_link',
          paywallType: paywallNavType,
          paywallOrder,
          email: values.email,
          clickIds,
          accessToken: data.accessToken,
        })
        if (data.accessToken) {
          trackRegistrationStartedMixpanel(
            data.accessToken,
            {
              auth_type: 'magic_link',
              'paywall-type': paywallNavType,
              'paywall-order': paywallOrder,
            },
            ab_testing,
            values.email,
            getRegistrationAttribution(),
          )
        }
        trackRegistrationCompleted('manual_otp', values.email)
        // Mongo _id decoded from the prelander JWT — identifies the Mixpanel
        // order_created fired for this purchase attempt.
        const userId = getUserIdFromAccessToken(data.accessToken)
        // Survive a round-trip through the Upgate hosted checkout: if the user
        // backs out and comes back, this is what reopens the popup with the token
        // they already hold instead of re-registering them (utils/session.js).
        saveLpSession({
          authToken: data.authToken,
          userId,
          email: values.email,
          source: 'magic_link',
        })
        // popup_first: the plan is already picked — skip the popup, open Upgate.
        if (isPopupFirst) {
          await startCheckoutWithPlan({
            authToken: data.authToken,
            accessToken: data.accessToken,
            userId,
            email: values.email,
          })
          return
        }
        setPopupState({
          authToken: data.authToken,
          accessToken: data.accessToken,
          userId,
        })
        return
      }

      if (data?.authToken && (data?.redirectUrl || data?.loginUrl)) {
        trackRegistrationCompleted('manual_otp')
        // Redirect arm: the platform spends the token on arrival, so there's
        // nothing left to resume — record the email only, so a visitor who comes
        // back gets it prefilled instead of retyping it into a form that can only
        // answer "email already used".
        saveLpSession({ email: values.email, source: 'magic_link' })

        if (onSuccessContinue) {
          // /quiz flow — keep user on prelander, store token for QuizFinal.
          localStorage.setItem('quizAuthToken', data.authToken)
          localStorage.removeItem('pendingGoogleSignup')
          sessionStorage.setItem('quizSessionAuthed', 'true')
          onSuccessContinue({ authToken: data.authToken })
          return
        }

        localStorage.setItem('isCompleted', true)
        window.location.href = data.redirectUrl || data.loginUrl
        return
      }

      setError(data?.message || 'Something went wrong')
    } catch (err) {
      setError(err?.message || 'Something went wrong')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()

    if (isMagicLink) {
      await handleEmailOnlySubmit()
      return
    }

    // Validate all fields
    if (!validateAll()) {
      return
    }

    setIsLoading(true)
    setError('')
    // Regular flow fires registration_started here; the paywall_popup flow fires it
    // AFTER a successful registration (funnel order) inside the success branch below.
    if (!usePaywallPopup) trackRegistrationStarted('manual')

    try {
      if (selections) {
        const userAnswers = await convertSelectionsToApiFormat(selections)
        console.log('✅ Всі відповіді користувача з ID:', userAnswers)

        Object.entries(userAnswers.attributes).forEach(([key, value]) => {
          console.log(`${key}: ${value}`)
        })
      }

      // Create account with selections for model-params
      const data = await createPreLanderAccount(values.email, values.password, selections, ab_testing)

      // Popup flow (standard signup): open in place instead of redirecting. Note this
      // MUST precede the redirectUrl check — the backend returns a redirectUrl we ignore.
      if (data.success && usePaywallPopup) {
        // registration_started BEFORE completed (funnel order): dataLayer + sGTM copies.
        trackRegistrationStarted({
          'paywall-type': paywallNavType,
          'paywall-order': paywallOrder,
          flow: 'paywall_popup',
          source: 'password',
          email: values.email,
          ...(clickIds || {}),
          ...getUtmAndEf(),
        })
        trackServerRegistrationStarted({
          flow: 'paywall_popup',
          source: 'password',
          paywallType: paywallNavType,
          paywallOrder,
          email: values.email,
          clickIds,
          accessToken: data.accessToken,
        })
        if (data.accessToken) {
          trackRegistrationStartedMixpanel(
            data.accessToken,
            {
              auth_type: 'password',
              'paywall-type': paywallNavType,
              'paywall-order': paywallOrder,
            },
            ab_testing,
            values.email,
            getRegistrationAttribution(),
          )
        }
        trackRegistrationCompleted('manual', values.email)
        // Mongo _id decoded from the prelander JWT — identifies the Mixpanel
        // order_created fired for this purchase attempt.
        const userId = getUserIdFromAccessToken(data.accessToken)
        // Survive the Upgate round-trip — see the magic-link branch above.
        saveLpSession({
          authToken: data.authToken,
          userId,
          email: values.email,
          source: 'password',
        })
        // popup_first: the plan is already picked — skip the popup, open Upgate.
        if (isPopupFirst) {
          await startCheckoutWithPlan({
            authToken: data.authToken,
            accessToken: data.accessToken,
            userId,
            email: values.email,
          })
          return
        }
        setPopupState({
          authToken: data.authToken,
          accessToken: data.accessToken,
          userId,
        })
        return
      }

      if (data.redirectUrl) {
        // Redirect arm — email only; see the magic-link branch above.
        if (data.success) saveLpSession({ email: values.email, source: 'password' })
        let redirectUrl = data.redirectUrl
        // create-friend-white is the SFW lander variant — every paywall handoff carries protection=sfw.
        // Also honor chat_paywall here in case the backend URL wasn't post-processed (e.g. no selections).
        try {
          const u = new URL(redirectUrl)
          if (paywallNavType === 'chat_paywall') u.pathname = resolvePaywallPath(paywallNavType)
          u.searchParams.set('protection', 'sfw')
          redirectUrl = u.toString()
        } catch {}
        window.location.href = redirectUrl
        return
      }

      if (data.success) {
        trackRegistrationCompleted('manual', values.email)
        // Redirect arm — email only; see the magic-link branch above.
        saveLpSession({ email: values.email, source: 'password' })

        if (onSuccessContinue) {
          // /quiz flow — keep the user on the page, save token for the final step
          if (data.authToken) {
            localStorage.setItem('quizAuthToken', data.authToken)
          }
          // User completed email signup — drop any prior "deferred Google" intent.
          localStorage.removeItem('pendingGoogleSignup')
          sessionStorage.setItem('quizSessionAuthed', 'true')
          onSuccessContinue({ authToken: data.authToken })
          return
        }

        const modelParams = selections ? await createModelParams(selections) : null
        const isPw = window.location.pathname.includes('/pw')
        // protection=sfw is added inside buildPaywallUrl — create-friend-white is the SFW lander variant.
        const paywallUrl = buildPaywallUrl(data.authToken, 'login_paywall', modelParams, selections?.welcome || 'Realistic', isPw && '2')

        localStorage.setItem('isCompleted', true)
        window.location.href = paywallUrl
      } else {
        setEmailError(data.message || 'Email already used')
      }
    } catch (err) {
      setError(err.message || 'Something went wrong')
    } finally {
      setIsLoading(false)
    }
  }

  const handleAcceptPrivacy = () => {
    setShowPrivacy(false)
  }

  const handleAcceptTerms = () => {
    setShowTerms(false)
  }

  // Get CSS classes for input styling
  const getEmailInputClass = () => {
    const emailProps = getFieldProps('email')
    if (emailProps.hasError) return 'form-input error-input'
    return 'form-input'
  }

  const getPasswordInputClass = () => {
    const passwordProps = getFieldProps('password')
    if (passwordProps.hasError) return 'form-input error-input'
    return 'form-input'
  }

  const handleSignIn = () => {
    if (onContinue) {
      onContinue()
    }
  }

  const { t } = useTranslation()

  const handleOtpVerified = async data => {
    trackRegistrationCompleted('manual_otp')

    // popup_first: a returning email verified by OTP still has a plan picked on this
    // visit — open its checkout instead of handing off to the platform. (The OTP
    // response carries no accessToken, so there's no Mongo _id: the Mixpanel copy of
    // order_created is skipped, the GTM/sGTM copies still fire.)
    const otpAuthToken = data?.authToken || data?.token
    if (isPopupFirst && otpAuthToken && getSelectedPlan()) {
      // Same reason as the in-place popup paths: this token is about to be spent
      // on Upgate, and a user who backs out must come back authenticated.
      saveLpSession({
        authToken: otpAuthToken,
        email: otpState?.email,
        source: 'magic_link',
      })
      await startCheckoutWithPlan({ authToken: otpAuthToken, email: otpState?.email })
      return
    }
    // Known account from here on. The token rides the redirect and is spent on
    // arrival, so store the email only — enough to prefill a returning visitor's
    // form instead of making them retype it (utils/session.js).
    saveLpSession({ email: otpState?.email, source: 'magic_link' })

    if (onSuccessContinue) {
      const authToken = data?.authToken || data?.token
      if (authToken) {
        localStorage.setItem('quizAuthToken', authToken)
      }
      localStorage.removeItem('pendingGoogleSignup')
      sessionStorage.setItem('quizSessionAuthed', 'true')
      onSuccessContinue({ authToken })
      return
    }

    localStorage.setItem('isCompleted', true)
    if (data?.redirectUrl || data?.loginUrl) {
      window.location.href = data.redirectUrl || data.loginUrl
    }
  }

  // popup_first: the paywall popup comes BEFORE this form. Rendered without an
  // authToken — nothing it displays needs a user (plans, system settings, geo
  // currency and the GB token bonuses are all anonymous reads), so the instant-token
  // amounts show exactly as they do for the authenticated after_signup render.
  // onCheckout captures the plan instead of charging, and trackOrder={false} defers
  // order_created to startCheckoutWithPlan.
  if (showPlanPopup) {
    return (
      <PaywallPopup
        videoSrc={paywallPopupVideo || undefined}
        paywallType={paywallNavType}
        paywallOrder={paywallOrder}
        onCheckout={handlePlanPicked}
        trackOrder={false}
        clickIds={clickIds}
      />
    )
  }

  // popup_first: registration is done and the Upgate order is being created — hold
  // the screen until the browser leaves for the hosted checkout page.
  if (isOpeningCheckout) {
    return (
      <QuizContent className='account-page'>
        <div className='account-container'>
          <div className='account-form-wrapper'>
            <p className='account-subtitle'>{t('loading')}</p>
          </div>
        </div>
      </QuizContent>
    )
  }

  if (popupState) {
    return (
      <PaywallPopup
        accessToken={popupState.accessToken}
        authToken={popupState.authToken}
        userId={popupState.userId}
        videoSrc={paywallPopupVideo || undefined}
        paywallType={paywallNavType}
        paywallOrder={paywallOrder}
        // A resumed session carries its own email (from storage); the in-place
        // registration paths use whatever was just typed into the form.
        email={popupState.email || values.email}
        clickIds={clickIds}
        // Resumed session only: fall back to the auth form if the stored token
        // turns out to be dead. (A post-registration popup keeps its existing
        // behaviour — the button re-enables and Continue can be retried.)
        onCheckoutError={popupState.resumed ? handleResumeCheckoutError : undefined}
      />
    )
  }

  if (otpState) {
    return (
      <QuizContent className='account-page'>
        <OtpStep
          email={otpState.email}
          selections={selections}
          ab_testing={ab_testing}
          initialOtpExpiry={otpState.otpExpiry}
          onBack={() => setOtpState(null)}
          onVerified={handleOtpVerified}
        />
      </QuizContent>
    )
  }

  return (
    <div>
      <QuizContent className={`account-page${isMagicLink ? ' account-page--magic' : ''}`}>
        <div className='account-container'>
          <div className='account-form-wrapper'>
            <div className='form-header'>
              {isQuiz ? (
                <>
                  <h2 className='account-title quiz-account-title'>
                    <>Create your account <br></br>to continue</>
                  </h2>
                </>
              ) : (
                <>
                  <h2 className='account-title'>{t('create_account')}</h2>
                  <p className='account-subtitle'>{t('she_waiting')}</p>
                </>
              )}
              <button className='close-button'>×</button>
            </div>

            <form onSubmit={handleSubmit} className='account-form'>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a ref={googleBtnRef} href='#' className='google-button'>
                <GoogleIcon />
                {isMagicLink ? 'Google' : t('google')}
              </a>

              <div className='separator'>
                {isQuiz || isMagicLink ? <span>{t('or')}</span> : <span>— {t('or')} —</span>}
              </div>

              <div className='input-wrapper'>
                <input
                  placeholder={t('email_label')}
                  type='email'
                  value={getFieldProps('email').value}
                  onChange={getFieldProps('email').onChange}
                  onBlur={getFieldProps('email').onBlur}
                  className={getEmailInputClass()}
                  required
                  aria-invalid={getFieldProps('email').hasError}
                  aria-describedby={getFieldProps('email').hasError ? 'create-email-error' : undefined}
                  autoComplete='email'
                />
              </div>
              {(getFieldProps('email').hasError || emailError) && (
                <div id='create-email-error' className='error-message-input'>
                  {getFieldProps('email').error || emailError}
                </div>
              )}
              {!isMagicLink && (
              <div className='cardStep15InputWrap'>
                <div className='input-wrapper'>
                  <input
                    placeholder={t('password_label')}
                    type={showPassword ? 'text' : 'password'}
                    value={getFieldProps('password').value}
                    onChange={getFieldProps('password').onChange}
                    onBlur={getFieldProps('password').onBlur}
                    className={getPasswordInputClass()}
                    required
                    minLength={6}
                    aria-invalid={getFieldProps('password').hasError}
                    aria-describedby={getFieldProps('password').hasError ? 'create-password-error' : undefined}
                    autoComplete='new-password'
                  />
                  <button
                    type='button'
                    className='cardStep15InputSvg'
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                    }}
                    aria-label={showPassword ? t('hide_password') : t('show_password')}
                  >
                    {showPassword ? (
                      <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'>
                        <path
                          d='M8 3.33333C11.4036 3.33333 13.6369 6.33656 14.3871 7.52455C14.4779 7.66833 14.5233 7.74023 14.5488 7.85112C14.5678 7.93439 14.5678 8.06578 14.5487 8.14905C14.5233 8.25993 14.4776 8.3323 14.3861 8.47705C14.1862 8.79343 13.8814 9.23807 13.4777 9.7203C12.7169 10.6667 11.4036 12.6667 8 12.6667C4.59644 12.6667 2.36319 9.66344 1.61292 8.47545C1.52212 8.33168 1.47672 8.25979 1.45131 8.14891C1.43221 8.06563 1.43222 7.93426 1.45133 7.85099C1.47675 7.74011 1.52236 7.66791 1.61357 7.52352C2.36369 6.33656 4.59694 3.33333 8 3.33333Z'
                          stroke='#9B9CAA'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                        <path
                          d='M8 6C9.10457 6 10 6.89543 10 8C10 9.10457 9.10457 10 8 10C6.89543 10 6 9.10457 6 8C6 6.89543 6.89543 6 8 6Z'
                          stroke='#9B9CAA'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    ) : (
                      <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'>
                        <path
                          d='M7.16196 3.39488C7.4329 3.35482 7.7124 3.33333 8.00028 3.33333C11.4036 3.33333 13.6369 6.33656 14.3871 7.52455C14.4779 7.66833 14.5233 7.74023 14.5488 7.85112C14.5678 7.93439 14.5678 8.06578 14.5487 8.14905C14.5233 8.25993 14.4776 8.3323 14.3861 8.47705C14.1862 8.79343 13.8814 9.23807 13.4777 9.7203M4.48288 4.47669C3.0415 5.45447 2.06297 6.81292 1.61407 7.52352C1.52286 7.66791 1.47725 7.74011 1.45183 7.85099C1.43273 7.93426 1.43272 8.06563 1.45181 8.14891C1.47722 8.25979 1.52262 8.33168 1.61342 8.47545C2.36369 9.66344 4.59694 12.6667 8.00028 12.6667C9.37255 12.6667 10.5546 12.1784 11.5259 11.5177M2.00028 2L14.0003 14M6.58606 6.58579C6.22413 6.94772 6.00028 7.44772 6.00028 8C6.00028 9.10457 6.89571 10 8.00028 10C8.55256 10 9.05256 9.77614 9.41449 9.41421'
                          stroke='#9B9CAA'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    )}
                  </button>
                </div>
                {getFieldProps('password').hasError && (
                  <div id='create-password-error' className='error-message-input'>
                    {getFieldProps('password').error}
                  </div>
                )}
              </div>
              )}
              {error && <div className='error-message'>{error}</div>}

              <button type='submit' className='submit-button' disabled={isLoading}>
                {isLoading ? t('creating_account') : t('create_account2')}
              </button>

              {!isQuiz && (
                <div className='terms-text'>
                  {t('by_signing_up')}{' '}
                  <span onClick={() => setShowTerms(true)} className='terms-link'>
                    {t('terms_of')}
                  </span>{' '}
                  <span onClick={() => setShowPrivacy(true)} className='terms-link'>
                    {t('privacy_policy')}
                  </span>
                </div>
              )}

              {!isMagicLink && (
                <div className='login-link'>
                  {t('already_have')}{' '}
                  <button type='button' onClick={handleSignIn} className='link-button'>
                    {t('log_in')} &gt;
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      </QuizContent>
      {showPrivacy && <PrivacyPolicy isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} onAccept={handleAcceptPrivacy} />}
      {showTerms && <TermsOfService isOpen={showTerms} onClose={() => setShowTerms(false)} onAccept={handleAcceptTerms} />}
    </div>
  )
}

export default Step14
